import lottie from 'lottie-web/build/player/lottie_light';
import { SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * The animation instance returned by `lottie.loadAnimation()`.
 * Pulled from lottie-web's own declarations so the type stays accurate.
 * @typedef {import('lottie-web').AnimationItem} AnimationItem
 */

/**
 * A class for the Illustration Hero module.
 * Supports playing an uploaded Lottie .json animation in place of the image.
 *
 * NOTE: lottie-web only reads Lottie JSON. It cannot open `.lottie` (dotLottie)
 * zip archives, so export or convert animations to a single .json file with
 * images embedded as base64 before uploading.
 */
export default class IllustrationHero extends MessageBus {
  /**
   * Constructor function of the IllustrationHero class. Must receive one argument,
   * a DOM node representing the illustration hero block element.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element DOM node passed into the constructor
     */
    this.element = element;

    /**
     * The lottie-web animation instance. Created lazily when the hero nears the viewport.
     * @property {AnimationItem|null} player - The active player instance or `null` when not created.
     */
    this.player = null;

    /**
     * The IntersectionObserver watching the Lottie container.
     * @property {IntersectionObserver|null} observer - The observer instance or `null` when not created.
     */
    this.observer = null;

    /**
     * Whether the Lottie container is currently within the viewport.
     * @property {boolean} inView - `true` when the animation is on screen.
     */
    this.inView = false;

    /**
     * Media query list for the user's reduced-motion preference.
     * @property {MediaQueryList} reducedMotionQuery - The reduced-motion media query.
     */
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers and
   * then create the event listeners.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();

    return this;
  }

  /**
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.lottieElement = this.element.querySelector(
      SELECTORS.ILLUSTRATION_HERO_LOTTIE
    );

    this.playerElement = this.element.querySelector(
      SELECTORS.ILLUSTRATION_HERO_LOTTIE_PLAYER
    );

    this.poster = this.element.querySelector(
      SELECTORS.ILLUSTRATION_HERO_LOTTIE_POSTER
    );

    this.imageContainer = this.element.querySelector(
      SELECTORS.ILLUSTRATION_HERO__IMAGE
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
    this.handlePlayerLoad = this.handlePlayerLoad.bind(this);
    this.handlePlayerLoadError = this.handlePlayerLoadError.bind(this);

    return this;
  }

  /**
   * Create event listeners to enable interaction with the view.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.lottieElement || !this.playerElement) return this;
    if (!this.lottieElement.dataset.lottieSrc) return this;

    // Reduced motion: keep the poster image and skip the animation entirely
    if (this.reducedMotionQuery.matches && this.poster) return this;

    this.bindIntersectionObserver();
    this.bindVisibilityEvents();
    this.bindReducedMotionEvents();

    return this;
  }

  // ---------------------------------------------------------------------------
  // Player
  // ---------------------------------------------------------------------------

  /**
   * Instantiates the lottie-web animation inside the cached player element.
   * Called once, the first time the hero scrolls near the viewport,
   * so off-screen heroes never download or parse the animation.
   *
   * NOTE: lottie-web renders an <svg> sized by `preserveAspectRatio`, which
   * mirrors the image styles. `xMidYMax slice` (cover, bottom-aligned) matches
   * the `image-in-background` treatment; `xMidYMid meet` (contain) is used
   * for `image-below`.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  createPlayer() {
    if (this.player) return this;

    // NOTE: the hero's height is set in CSS (`.illustration-hero__image
    // .image-in-background.has-lottie`), not here. Writing an inline pixel
    // height at this point froze the hero at the width the page happened to
    // load at, so resizing the window wider revealed the edge of the artwork
    // until the next reload.

    const { lottieSrc, lottieLoop, lottieFit } = this.lottieElement.dataset;

    this.player = lottie.loadAnimation({
      container: this.playerElement,
      renderer: 'svg',
      loop: lottieLoop !== 'false',
      autoplay: false,
      path: lottieSrc,
      rendererSettings: {
        preserveAspectRatio:
          lottieFit === 'cover' ? 'xMidYMax slice' : 'xMidYMid meet',
        progressiveLoad: true
      }
    });

    this.player.addEventListener('DOMLoaded', this.handlePlayerLoad);
    this.player.addEventListener('data_failed', this.handlePlayerLoadError);

    return this;
  }

  /**
   * Destroys the player and observer and restores the poster image.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  destroyPlayer() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.player) {
      this.player.destroy();
      this.player = null;
    }

    this.lottieElement.classList.remove('is-loaded');

    return this;
  }

  /**
   * Adds the loaded class so CSS fades the animation in over the poster,
   * then starts playback if the hero is on screen.
   */
  handlePlayerLoad() {
    this.lottieElement.classList.add('is-loaded');
    this.syncPlayback();
  }

  /**
   * Flags the container so CSS hides the player and leaves the poster visible.
   */
  handlePlayerLoadError() {
    this.lottieElement.classList.add('has-error');

    // eslint-disable-next-line no-console
    console.error(
      '[IllustrationHero] Lottie failed to load:',
      this.lottieElement.dataset.lottieSrc
    );
  }

  // ---------------------------------------------------------------------------
  // Playback state
  // ---------------------------------------------------------------------------

  /**
   * Plays the animation when the hero is on screen and the tab is visible,
   * otherwise pauses it to save CPU and battery.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  syncPlayback() {
    if (!this.player || !this.player.isLoaded) return this;

    if (this.inView && !document.hidden) {
      this.player.play();
    } else {
      this.player.pause();
    }

    return this;
  }

  // ---------------------------------------------------------------------------
  // Viewport
  // ---------------------------------------------------------------------------

  /**
   * Handles intersection changes for the Lottie container.
   * Lazily creates the player on first approach, then syncs playback.
   * @param {IntersectionObserverEntry[]} entries - The observed intersection entries.
   */
  handleIntersection([entry]) {
    this.inView = entry.isIntersecting;

    if (this.inView && !this.player) {
      this.createPlayer();
      return;
    }

    this.syncPlayback();
  }

  /**
   * Creates the IntersectionObserver for the Lottie container.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  bindIntersectionObserver() {
    this.observer = new IntersectionObserver(this.handleIntersection, {
      rootMargin: '200px 0px',
      threshold: 0
    });

    this.observer.observe(this.lottieElement);

    return this;
  }

  /**
   * Pauses the animation when the browser tab is hidden, resumes when visible.
   */
  handleVisibilityChange() {
    this.syncPlayback();
  }

  /**
   * Adds the visibilitychange listener to the document.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  bindVisibilityEvents() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    return this;
  }

  // ---------------------------------------------------------------------------
  // Reduced motion
  // ---------------------------------------------------------------------------

  /**
   * Stops the animation if the user enables reduced motion mid-session.
   * @param {MediaQueryListEvent} event - The media query change event.
   */
  handleReducedMotionChange(event) {
    if (!event.matches || !this.poster) return;

    this.destroyPlayer();
    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );
  }

  /**
   * Adds the change listener to the reduced-motion media query.
   * @returns {IllustrationHero} A reference to the current instance of the class
   * @chainable
   */
  bindReducedMotionEvents() {
    this.reducedMotionQuery.addEventListener(
      'change',
      this.handleReducedMotionChange
    );

    return this;
  }
}
